import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import DataTable from "../../common/DataTable/DataTable";
import "../../common/DataTable/DataTable.css";
import Dropdown from "../../common/Dropdown/Dropdown";
import PageErrorState from "../../common/PageErrorState/PageErrorState";
import EntityConstants from "../../configs/EntityConstants";
import { GET } from "../../service/CRUDService";
import {
  classifyApiError,
  getApiErrorCopy,
} from "../../utils/classifyApiError";
import { formatDateOnly } from "../../utils/formatDate";
import {
  getAvatarColors,
  getHolderInitials,
  parseHoldersListPayload,
} from "./utils/holderUtils";

const PAGE_LIMIT = 8;

const HoldersListPage = () => {
  const { t } = useTranslation(["holders", "finance", "history"]);
  const navigate = useNavigate();
  const requestIdRef = useRef(0);

  const [holders, setHolders] = useState([]);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedProgramme, setSelectedProgramme] = useState("");
  const [selectedBatch, setSelectedBatch] = useState("");
  const [programmes, setProgrammes] = useState([]);
  const [batches, setBatches] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    GET(`${EntityConstants.GET_PROGRAMME_DETAILS}?limit=100&offset=0`)
      .then((response) => response.json())
      .then((data) => {
        if (data?.error) return;
        const activeProgrammes = (data?.data?.results || []).filter(
          (programme) => programme.is_active,
        );
        setProgrammes(activeProgrammes);
      })
      .catch((err) => {
        console.error("Error fetching programmes for holders filter:", err);
      });
  }, []);

  useEffect(() => {
    if (!selectedProgramme) {
      setBatches([]);
      return;
    }

    GET(
      `${EntityConstants.GET_BATCH_DETAILS}?programme_code=${encodeURIComponent(
        selectedProgramme,
      )}&limit=100&offset=0`,
    )
      .then((response) => response.json())
      .then((data) => {
        if (data?.error) {
          setBatches([]);
          return;
        }
        const activeBatches = (data?.data?.results || []).filter(
          (batch) => batch.is_active,
        );
        setBatches(activeBatches);
      })
      .catch((err) => {
        console.error("Error fetching batches for holders filter:", err);
        setBatches([]);
      });
  }, [selectedProgramme]);

  const loadHolders = useCallback(
    async (nextOffset = 0) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          limit: String(PAGE_LIMIT),
          offset: String(nextOffset),
        });
        if (debouncedSearch) {
          params.set("search", debouncedSearch);
        }
        if (selectedProgramme) {
          params.set("programme_code", selectedProgramme);
        }
        if (selectedBatch) {
          params.set("batch_code", selectedBatch);
        }

        const response = await GET(
          `${EntityConstants.GET_HOLDERS}?${params.toString()}`,
        );
        const payload = await response.json();
        if (requestId !== requestIdRef.current) return;

        if (!response.ok) {
          throw Object.assign(new Error(payload?.detail || payload?.message), {
            status: response.status,
          });
        }

        const parsed = parseHoldersListPayload(payload);
        setHolders(parsed.results);
        setNext(parsed.next);
        setPrevious(parsed.previous);
        setOffset(nextOffset);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        setHolders([]);
        setNext(null);
        setPrevious(null);
        setError(classifyApiError(err));
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [debouncedSearch, selectedProgramme, selectedBatch],
  );

  useEffect(() => {
    loadHolders(0);
  }, [loadHolders]);

  const programmeOptions = useMemo(
    () => [
      { name: t("holders:all_programs"), value: "" },
      ...programmes
        .map((programme) => ({
          name: programme.programme_name,
          value: programme.programme_code,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    ],
    [programmes, t],
  );

  const batchOptions = useMemo(
    () => [
      { name: t("holders:all_batches"), value: "" },
      ...batches
        .map((batch) => ({
          name: batch.batch_name,
          value: batch.batch_code,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    ],
    [batches, t],
  );

  const selectedProgrammeOption =
    programmeOptions.find((o) => o.value === selectedProgramme) ||
    programmeOptions[0];
  const selectedBatchOption =
    batchOptions.find((o) => o.value === selectedBatch) || batchOptions[0];

  const columns = useMemo(
    () => [
      {
        key: "holder_name",
        label: t("holders:column_holder"),
        render: (_value, row) => {
          const initials = getHolderInitials(row.holder_name);
          const colors = getAvatarColors(
            row.holder_public_id || row.email || row.holder_name,
          );
          return (
            <div className="flex w-full items-center justify-start gap-3 min-w-[14rem] text-left">
              <div
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold"
                style={{
                  backgroundColor: colors.bg,
                  color: colors.text,
                }}
                aria-hidden
              >
                {initials}
              </div>
              <div className="min-w-0 text-left">
                <p className="truncate font-medium text-cfHeadingText">
                  {row.holder_name || t("holders:na")}
                </p>
                {row.email ? (
                  <p className="truncate text-sm text-gray-500">{row.email}</p>
                ) : null}
              </div>
            </div>
          );
        },
      },
      {
        key: "programme_name",
        label: t("holders:column_program"),
        render: (value) => value || t("holders:na"),
      },
      {
        key: "batch_name",
        label: t("holders:column_batch"),
        render: (value) => value || t("holders:na"),
      },
      {
        key: "credentials_count",
        label: t("holders:column_credentials"),
        render: (value) => (
          <span className="font-semibold text-cfHeadingText">{value ?? 0}</span>
        ),
      },
      {
        key: "last_issued_at",
        label: t("holders:column_last_issued"),
        render: (value) => formatDateOnly(value, t("holders:na")),
      },
    ],
    [t],
  );

  const errorCopy = error ? getApiErrorCopy(error, t, "finance") : null;
  const hasActiveFilters =
    Boolean(debouncedSearch) ||
    Boolean(selectedProgramme) ||
    Boolean(selectedBatch);

  return (
    <div className="flex flex-col items-center w-full mt-20 bg-cfSecondary min-h-screen pb-3">
      <div className="w-full bg-cfSearchContainer flex justify-center py-[0.7rem] px-4">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between w-full gap-2 lg:gap-4">
          <p className="text-cfHeadingText font-semibold self-start my-auto min-h-[2.5rem] lg:min-h-0 flex items-center">
            {t("holders:title")}
          </p>
          <form
            onSubmit={(e) => e.preventDefault()}
            className="bg-cfSecondary p-2 rounded-lg w-full lg:w-[25rem] transition-shadow duration-150 focus-within:ring-2 focus-within:ring-cfPrimaryButton focus-within:ring-offset-2"
          >
            <input
              type="text"
              name="search"
              id="holders-input-search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t("holders:search_placeholder")}
              aria-label={t("holders:search_placeholder")}
              className="outline-none border-none p-0 placeholder:text-sm w-full focus-visible:outline-none"
              style={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}
            />
          </form>
        </div>
      </div>

      <div className="w-full mt-[0.6rem] px-4 md:px-8">
        <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 w-full min-w-0 sm:items-center mb-2">
          <div className="w-full sm:w-[13rem] min-w-0 h-10">
            <Dropdown
              options={programmeOptions}
              selected={selectedProgrammeOption}
              onSelect={(value) => {
                setSelectedProgramme(value || "");
                setSelectedBatch("");
              }}
              buttonClass="h-10 px-4 py-0 border-gray-300 rounded-md w-full min-w-0 items-center"
              menuClass="z-20 w-full"
              testId="holders-programme-filter"
            />
          </div>
          <div className="w-full sm:w-[13rem] min-w-0 h-10">
            <Dropdown
              options={batchOptions}
              selected={selectedBatchOption}
              onSelect={(value) => setSelectedBatch(value || "")}
              buttonClass="h-10 px-4 py-0 border-gray-300 rounded-md w-full min-w-0 items-center"
              menuClass="z-20 w-full"
              testId="holders-batch-filter"
            />
          </div>
        </div>

        {error ? (
          <PageErrorState
            kind={error.kind}
            title={t("holders:load_error_title")}
            description={errorCopy?.description}
            primaryLabel={t("holders:retry")}
            onPrimary={() => loadHolders(offset)}
            variant="section"
          />
        ) : (
          <DataTable
            columns={columns}
            data={holders}
            loading={loading}
            loadingVariant="skeleton"
            skeletonRowCount={PAGE_LIMIT}
            emptyMessage={
              hasActiveFilters
                ? t("holders:empty_filtered")
                : t("holders:empty_title")
            }
            emptyHint={
              hasActiveFilters
                ? t("holders:empty_filtered_hint")
                : t("holders:empty_description")
            }
            rowKey="holder_public_id"
            align="center"
            dataTestId="holders-list-table"
            hasPrevious={Boolean(previous)}
            hasNext={Boolean(next)}
            onPrevious={() => loadHolders(Math.max(0, offset - PAGE_LIMIT))}
            onNext={() => loadHolders(offset + PAGE_LIMIT)}
            showPagination
            onRowClick={(row) => {
              if (!row.holder_public_id) return;
              navigate(`/issuer/holders/${row.holder_public_id}`);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default HoldersListPage;
